// Pure package / expiry / display / date / guard logic.
// No React, no Firebase, no ./App. DISPLAY + GUARDS ONLY — nothing here is
// persisted, and it never recomputes money or session counts from the
// payments / attendance collections. See docs/superpowers/specs/2026-09-03-v3.7.0-correctness-design.md.

function pkgTypeOf(raw) {
  const t = raw && raw.type;
  if (!t || t === 'none') return 'none';
  if (t === 'custom') return 'sessions';
  if (t === 'template_sessions') return raw.templateBoth ? 'both' : 'sessions';
  if (t === 'template_duration') return raw.templateBoth ? 'both' : 'duration';
  if (['weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'].includes(t)) return t;
  return 'none';
}

const pad = (n) => String(n).padStart(2, '0');
function clientToday(now = new Date()) { return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`; }
function clientMonth(now = new Date()) { return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`; }
function clientYear(now = new Date()) { return String(now.getFullYear()); }
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return clientToday(new Date(y, m - 1, d + n));
}
function addMonthsClamped(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(y, m - 1 + n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  t.setDate(Math.min(d, last));
  return clientToday(t);
}

function toMins(str) {
  if (typeof str !== 'string') return null;
  const m = str.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return null;
  let h = Number(m[1]); const min = Number(m[2]); const ap = m[3] && m[3].toLowerCase();
  if (min > 59) return null;
  if (ap) { if (h < 1 || h > 12) return null; h = ap === 'am' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12); }
  else if (h > 23) return null;
  return h * 60 + min;
}

// Replicates App.js getActivePkg EXACTLY, including the dangling-pointer case:
// if packageHistory + currentPackageId are set but the id isn't found -> null
// (do NOT fall through to client.package).
function rawPackage(client) {
  if (client && client.packageHistory && client.packageHistory.length && client.currentPackageId) {
    const hit = client.packageHistory.find((p) => p.packageId === client.currentPackageId);
    return hit ? { raw: hit, packageId: client.currentPackageId } : null;
  }
  if (client && client.package && client.package.type && client.package.type !== 'none') {
    return { raw: { ...client.package, status: client.package.status || 'active' }, packageId: 'legacy' };
  }
  return null;
}

const num = (v) => Number(v) || 0;

function priceOf(raw, client, weeks) {
  if (num(client && client.packagePrice)) return num(client.packagePrice);
  if (num(raw.totalAmount)) return num(raw.totalAmount);
  const per = num(raw.pricePerWeek) || num(raw.pricePerMonth) || num(raw.pricePerQuarter) || num(raw.pricePerHalfYear) || num(raw.pricePerYear);
  if (per && raw.type === 'weekly') return per * (weeks || 1);
  return per || 0;
}

function normalizePackage(client) {
  const found = rawPackage(client);
  if (!found) return null;
  const { raw, packageId } = found;
  const type = pkgTypeOf(raw);
  if (type === 'none') return null;
  const weeks = num(raw.weeks);
  return {
    packageId, type,
    startDate: raw.startDate || null,
    weeks,
    durationDays: num(raw.templateDurationDays),
    customDurationDays: num(raw.customDuration),
    sessionsTotal: num(raw.customClasses) || num(raw.templateSessions),
    price: priceOf(raw, client, weeks),
    extendedDays: num(raw.extendedDays),
    status: raw.status || 'active',
  };
}

// Returns null (not a computed date) when the type needs a weeks/days value
// that is 0 or absent. "Never expires" is the safe default; "expires the day it
// starts" would lock a coach out of their own client.
const MONTHS_FOR = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
function packageExpiry(n) {
  if (!n || !n.startDate) return null;
  const ext = n.extendedDays || 0;
  if (MONTHS_FOR[n.type]) return addDays(addMonthsClamped(n.startDate, MONTHS_FOR[n.type]), ext);
  if (n.type === 'weekly') return n.weeks > 0 ? addDays(n.startDate, n.weeks * 7 + ext) : null;
  if (n.type === 'duration' || n.type === 'both') return n.durationDays > 0 ? addDays(n.startDate, n.durationDays + ext) : null;
  // Pure session packages are gated on the session count, never a date — matches
  // the old getClientExpiry (a numeric customDuration from an old import must not
  // lock out a client who still has sessions left).
  return null;
}
function packageExpiryOf(client) { return packageExpiry(normalizePackage(client)); }

// Everything a screen needs to display a client's package + balance. Reads the
// STORED cache fields (client.paid, client.classesRemaining) — never recomputes
// from the payments / attendance collections. Nothing here is persisted.
// The signed price/paid tail, shared by both returns below.
function money(price, paid) {
  const balance = price - paid;
  return { price, paid, balance, credit: Math.max(0, -balance), amountDue: Math.max(0, balance), isPaidUp: balance <= 0 };
}
function packageDisplayState(client, opts = {}) {
  const today = clientToday(opts.now);
  const n = normalizePackage(client);
  const paid = num(client && client.paid);
  const classesAttended = num(client && client.classesAttended);

  if (!n) {
    return { active: false, type: 'none', isSessionBased: false, startDate: null, expiryDate: null,
      isExpired: false, sessionsTotal: 0, classesRemaining: null, classesAttended, ...money(0, paid) };
  }

  const isSessionBased = n.type === 'sessions' || n.type === 'both';
  const expiryDate = packageExpiry(n);
  const classesRemaining = isSessionBased ? num(client.classesRemaining) : null;

  const isExpired =
    n.status === 'expired' ||
    (!!expiryDate && today > expiryDate) ||
    (isSessionBased && n.sessionsTotal > 0 && classesRemaining <= 0);

  return { active: true, type: n.type, isSessionBased, startDate: n.startDate, expiryDate,
    isExpired, sessionsTotal: n.sessionsTotal, classesRemaining, classesAttended, ...money(n.price, paid) };
}

function isSessionBased(client) {
  const n = normalizePackage(client);
  return !!n && (n.type === 'sessions' || n.type === 'both');
}
function sessionsTotalOf(client) {
  const n = normalizePackage(client);
  return n ? n.sessionsTotal : 0;
}

function canMarkAttendance(client, dateStr, opts = {}) {
  const { existingForClient = [], time, now } = opts;
  if (dateStr > clientToday(now)) return { ok: false, reason: 'future' };
  // dedup replicates today's check exactly: match by (clientId, date) and, when a
  // time is given, exact time. Do NOT block a timed mark just because an untimed
  // record exists that day.
  if (existingForClient.some((a) => a && a.clientId === client.id && a.date === dateStr && (!time || a.time === time)))
    return { ok: false, reason: 'duplicate' };
  const s = packageDisplayState(client, { now });
  if (s.active) {
    if (s.isSessionBased && s.sessionsTotal > 0 && s.classesRemaining <= 0) return { ok: false, reason: 'no-sessions' };
    if (s.expiryDate && dateStr > s.expiryDate) return { ok: false, reason: 'expired' };
  }
  return { ok: true };
}

const CURRENT_TAGS = new Set(['legacy', 'unlinked']);
// Does this payment count toward client.paid? Preserves today's invariant
// (client.paid = paid-toward-current-package) without a collection scan — the
// payment being changed is passed in directly.
function paymentIsCurrent(payment, client) {
  const cur = client && client.currentPackageId;
  const tag = payment && payment.packageId;
  const hist = (client && client.packageHistory) || [];
  const hasRenewed = hist.length > 1;
  if (cur && tag && !CURRENT_TAGS.has(tag)) {
    if (tag === cur) return true;
    if (hist.some((p) => p.packageId === tag)) return false; // a known prior package
    return !hasRenewed; // unknown/typo tag — fall back so it stays editable
  }
  return !hasRenewed; // untagged / legacy / unlinked: current only if never renewed
}

// Roll the active package into history and make `newPkg` the active one — the
// shared shape used by package renewal and by lead→client reconversion of a
// former client. The entry that was current becomes "renewed"; a legacy client
// (no packageHistory) gets its c.package folded in as a "legacy" renewed entry
// first. Returns { packageHistory, currentPackageId } only — the caller composes
// paid / classesRemaining / package around it.
function rolloverPackageHistory(client, newPkg, newId, today = clientToday()) {
  const c = client || {};
  const cleanPkg = { ...(newPkg || {}), extendedDays: 0 };
  const price = num(cleanPkg.totalAmount);
  const newEntry = { packageId: newId, ...cleanPkg, status: 'active', totalAmount: price, startedAt: today };
  const prior = (c.packageHistory && c.packageHistory.length)
    ? c.packageHistory.map((p) => (p.packageId === c.currentPackageId ? { ...p, status: 'renewed' } : p))
    : ((c.package && c.package.type && c.package.type !== 'none')
        ? [{ packageId: 'legacy', ...c.package, status: 'renewed', totalAmount: num(c.package.totalAmount) || num(c.packagePrice) }]
        : []);
  return { packageHistory: [...prior, newEntry], currentPackageId: newId };
}

// null (unset), undefined, and non-numeric all fall back to IST (+330) — the
// dominant user base, so the user still gets nudges, never at 3am local.
// Number(null) is 0 (a finite number), so the null check must be explicit.
// This is the ONLY place the offset is normalized; localHourOf/localDateOf take
// an already-finite number.
function tzOffset(settings) {
  const raw = settings && settings.timezoneOffset;
  return raw != null && Number.isFinite(Number(raw)) ? Number(raw) : 330;
}

// The user's local wall-clock hour now. offsetMin = minutes east of UTC (a
// finite number — pass through tzOffset). Handles :30/:45 zones and day wrap.
function localHourOf(offsetMin, now) {
  const localMin = (((now.getUTCHours() * 60 + now.getUTCMinutes() + offsetMin) % 1440) + 1440) % 1440;
  return Math.floor(localMin / 60);
}

// The user's local calendar date, "YYYY-MM-DD" — the notif_log dedupe key, so
// the 2-hour window can't double-send across UTC midnight. Formats the shifted
// instant's UTC parts directly (host-timezone-agnostic).
function localDateOf(offsetMin, now) {
  const s = new Date(now.getTime() + offsetMin * 60000);
  return `${s.getUTCFullYear()}-${pad(s.getUTCMonth() + 1)}-${pad(s.getUTCDate())}`;
}

const HOUR_TO_NUDGE = { 7: 'morning', 12: 'midday', 18: 'evening' };
// Which nudge (if any) is due for this user right now, and whether this is the
// catch-up (second) hour of its 2-hour window. `{ type, catchUp, localDate } | null`.
// Safe because the target hours (7/12/18) are never adjacent, so `h` and `h-1`
// can't both hit.
function nudgeDueThisHour(settings, now) {
  const off = tzOffset(settings);
  const h = localHourOf(off, now);
  const type = HOUR_TO_NUDGE[h] || HOUR_TO_NUDGE[h - 1];
  if (!type) return null;
  return { type, catchUp: !HOUR_TO_NUDGE[h], localDate: localDateOf(off, now) };
}

module.exports = {
  pkgTypeOf,
  clientToday, clientMonth, clientYear, addDays, addMonthsClamped,
  toMins,
  rawPackage, normalizePackage,
  packageExpiry, packageExpiryOf,
  packageDisplayState,
  isSessionBased, sessionsTotalOf, canMarkAttendance, paymentIsCurrent,
  rolloverPackageHistory,
  localHourOf, localDateOf, nudgeDueThisHour,
};
